import {
	useIsFocused,
	useNavigation,
	useNavigationState,
	useRoute,
} from '@react-navigation/native';
import React, { useContext, useEffect, useRef, useState } from 'react';
import {
	Image,
	ImageBackground,
	PixelRatio,
	StatusBar,
	StyleSheet,
	View,
	ViewStyle,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';

import { BgLandscapePng, BgPortraitPng, VipSubscriptionBackground } from '../../assets';
import { ThemeContext } from '../../config';
import { getSocket } from '../../services/socket';
import { RootState } from '../../store';
import { toast } from '../../utils/helpers/toastUtils';
import { usePaymentStatus } from '../../utils/hooks/paymentStatus';
import { useDevice } from '../../utils/hooks/useDevice';
import { useProfileRefreshOnPayment } from '../../utils/hooks/useProfileRefreshOnPayment';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import { isFactorTv, isWebBased } from '../../utils/platform';
import { playSound } from '../../utils/sound';
import { AppFooter, Sidebar, TopSection } from '../Components';
import { useDesktopLayout } from '../Components/Desktop/desktopLayout';
import DesktopTopBar from '../Components/Desktop/DesktopTopBar';
import BonusGamesModal from '../Components/Modals/BonusGamesComponents/BonusGamesModal';
import { GameRecoveryModal } from '../Components/Modals/GameRecoveryModal';
import InviteNotificationModal from '../Components/Modals/InviteNotificationModal';
import { PaymentStatusModal } from '../Components/Modals/PaymentStatusModal';
import { FooterTopContext } from './footerHeight';
import { HeaderTopOffsetContext } from './headerOffset';

type Nav = any;

// The screens that render a MainLayout of their own - i.e. bring their own header and footer.
const MAIN_LAYOUT_ROUTES = [
	'Main',
	'Lobby',
	'Tournaments',
	'Tournament',
	'Room',
	'ActiveTournament',
	'Duels',
	'DuelResult',
	'DuelScoreBreakdown',
	'DuelHistory',
	'DuelSeason',
	'Event',
	'Friends',
	'Bonuses',
	'Ranking',
];

const DESKTOP_EXTRA_LAYOUT_ROUTES = ['VipRooms', 'VipSubscription', 'VipActiveMember'];

type InviteData = {
	name: string;
	username: string;
	table: string;
	table_name: string;
	url: string;
	inviteId: string;
	legacyRoomId?: number;
};

type MainLayoutProps = {
	children: React.ReactNode;
	centerContent?: boolean;
	/**
	 * The screen draws against the whole viewport and the header and footer float over it, the
	 * way the home redesign does - for screens whose own content has to run edge to edge (a list
	 * that keeps scrolling behind the nav, say) rather than sit in the box left between the two.
	 * Such a screen owes itself the clearance: HEADER_ROW_HEIGHT at the top, getNavRowHeight() at
	 * the bottom.
	 */
	fullBleed?: boolean;
};

const MainLayoutView: React.FC<MainLayoutProps> = ({
	children,
	centerContent = false,
	fullBleed = false,
}) => {
	const navigation = useNavigation<Nav>();
	const isFocused = useIsFocused();
	const buttonRef = useRef<{ focus: () => void } | null>(null);
	const { theme, dark } = useContext(ThemeContext);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [isBonusGamesModalOpen, setIsBonusGamesModalOpen] = useState(false);
	const [bottomBarHeight, setBottomBarHeight] = useState(0);
	// The footer's top edge in window coordinates, for screens that end their content above it.
	const footerRef = useRef<View>(null);
	const [footerTop, setFooterTop] = useState<number | null>(null);
	const { width, height, insets } = useSafeViewport();
	const [showRecoveryModal, setShowRecoveryModal] = useState(false);
	const [inviteData, setInviteData] = useState<InviteData | null>(null);
	const [showInviteNotification, setShowInviteNotification] = useState(false);
	const paymentData = usePaymentStatus();
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const playerId = useSelector((s: RootState) => s.auth.user?.id || '');
	const username = useSelector((s: RootState) => s.auth.user?.username || '');
	const { isDesktop, isWeb, isXLg, isAndroid } = useDevice();
	// The desktop redesign (Figma "BOGI DRAFT" / "Desktop") has its own chrome: a top bar with the
	// wallet centred and a full-width dock, with the screen laid out in the box between them.
	const desktop = useDesktopLayout();
	const isDesktopLayout = desktop.isDesktopLayout;
	const focusedRouteName = useNavigationState(
		state => state.routes[state.index ?? state.routes.length - 1]?.name,
	);
	const contentMinHeight = isXLg ? height * 0.75 : isDesktop ? height * 0.68 : undefined;

	const hasOverlayBottomBar = isAndroid;
	// On web every screen in the stack stays mounted (the blurred one just gets
	// `display: none`), so a second copy of the header and footer sits in the DOM carrying the
	// same SVG fragment ids as the visible one. `url(#id)` resolves to the first match in
	// document order - the hidden copy - and the paint silently fails: the jackpot gradient
	// goes flat, the missions / free-chips patterns and the nav-base filter disappear. So a
	// blurred screen drops its chrome, but only when whatever sits on top brings its own -
	// the modal screens are transparent presentations with no chrome of their own.
	// The desktop chrome's dock draws exported SVG art with fixed filter ids, so on desktop the
	// three VIP screens - which bring their own MainLayout but are missing from that list - have
	// to count too, or the Home plate under them paints from the hidden copy and disappears.
	const chromeRoutes = isDesktopLayout
		? [...MAIN_LAYOUT_ROUTES, ...DESKTOP_EXTRA_LAYOUT_ROUTES]
		: MAIN_LAYOUT_ROUTES;
	const showChrome = !isWeb || isFocused || !chromeRoutes.includes(focusedRouteName ?? '');

	useEffect(() => {
		if (paymentData.status) {
			setShowPaymentModal(true);
		}
	}, [paymentData.status]);

	// Pull the fresh chips balance in after a successful purchase (no reload needed).
	useProfileRefreshOnPayment(paymentData.status, paymentData.depositId);

	useEffect(() => {
		PixelRatio.get();
		PixelRatio.getFontScale();
		if (isWebBased && isFactorTv && buttonRef?.current) buttonRef.current.focus();
	}, []);

	useEffect(() => {
		if (isWeb) {
			const html = document.documentElement;
			const body = document.body;

			const prevHtmlH = html.style.height;
			const prevBodyH = body.style.height;
			const prevHtmlMinH = html.style.minHeight;
			const prevBodyMinH = body.style.minHeight;
			const prevBodyOverflow = body.style.overflow;

			html.style.height = '100%';
			html.style.minHeight = '100%';
			html.style.overflow = 'hidden';
			body.style.height = '100%';
			body.style.minHeight = '100%';
			body.style.overflow = 'hidden';

			return () => {
				html.style.height = prevHtmlH;
				html.style.minHeight = prevHtmlMinH;
				body.style.height = prevBodyH;
				body.style.minHeight = prevBodyMinH;
				body.style.overflow = prevBodyOverflow;
			};
		}
	}, [isWeb]);

	// The screen this layout belongs to, not whichever one is on top of the stack: keyed off the
	// top, Home dropped out of its full-bleed layout - and visibly jumped down - the moment a tab
	// was tapped, while it was still on screen under the next one fading in.
	const routeName = useRoute().name;
	// Every screen in the redesign sits on the same backdrop (baked from the Figma "Background"
	// frame, 2747:16895 - red gradient, suit pattern, top glow), so there is no per-route
	// artwork any more. RootNavigator paints the same source underneath for the screens that
	// have no MainLayout, and the two must stay in step: when they disagreed you could see one
	// background through the other. Landscape keeps its own asset - the design has no landscape
	// frame.
	const isMobileLayout = !isDesktop;
	const backgroundSource = isMobileLayout ? BgPortraitPng : BgLandscapePng;
	const showVipBackgroundOverlay = isMobileLayout && routeName === 'VipSubscription';

	const handleClosePaymentModal = () => {
		setShowPaymentModal(false);
	};

	const [servicesRecovering, setServicesRecovering] = useState({
		websocket: false,
		game: false,
	});

	// Debounce the recovery modal so brief backend health-probe flaps (which recover within one
	// ~5s health cycle) never surface a modal. Only a sustained outage shows the modal.
	const recoveryModalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	// Same debounce for our own socket being down (GDD §12.1: modal after 6s disconnected).
	const socketRecoveryModalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const RECOVERY_MODAL_DELAY_MS = 6000;

	useEffect(() => {
		if (!servicesRecovering.websocket && !servicesRecovering.game) {
			setShowRecoveryModal(false);
		} else {
			setShowRecoveryModal(true);
		}
	}, [servicesRecovering]);

	useEffect(() => {
		if (!playerId || !username) return;

		const socket = getSocket(String(playerId), String(username));

		const handleGameRecovering = () => {
			// Wait out brief flaps; only show the modal if the service is still down after the delay.
			if (recoveryModalTimerRef.current) return;
			recoveryModalTimerRef.current = setTimeout(() => {
				console.warn('[GameRecovery] service still recovering - showing modal');
				setServicesRecovering(prev => ({ ...prev, game: true }));
				recoveryModalTimerRef.current = null;
			}, RECOVERY_MODAL_DELAY_MS);
		};

		const handleGameRecovered = () => {
			// Recovered (possibly within the debounce window): cancel any pending show and hide.
			if (recoveryModalTimerRef.current) {
				clearTimeout(recoveryModalTimerRef.current);
				recoveryModalTimerRef.current = null;
			}
			setServicesRecovering(prev => ({ ...prev, game: false }));
		};

		// Socket connection health: services/socket.ts dispatches these as window events on web.
		const handleWebSocketRecovering = () => {
			if (socketRecoveryModalTimerRef.current) return;
			socketRecoveryModalTimerRef.current = setTimeout(() => {
				console.warn('[GameRecovery] socket still disconnected - showing modal');
				setServicesRecovering(prev => ({ ...prev, websocket: true }));
				socketRecoveryModalTimerRef.current = null;
			}, RECOVERY_MODAL_DELAY_MS);
		};

		const handleWebSocketReady = () => {
			if (socketRecoveryModalTimerRef.current) {
				clearTimeout(socketRecoveryModalTimerRef.current);
				socketRecoveryModalTimerRef.current = null;
			}
			setServicesRecovering(prev => ({ ...prev, websocket: false }));
		};

		//  Game service health
		socket.on('game:recovering', handleGameRecovering);
		socket.on('game:recovered', handleGameRecovered);
		window.addEventListener('websocket:recovering', handleWebSocketRecovering);
		window.addEventListener('websocket:ready', handleWebSocketReady);

		return () => {
			if (recoveryModalTimerRef.current) {
				clearTimeout(recoveryModalTimerRef.current);
				recoveryModalTimerRef.current = null;
			}
			if (socketRecoveryModalTimerRef.current) {
				clearTimeout(socketRecoveryModalTimerRef.current);
				socketRecoveryModalTimerRef.current = null;
			}
			socket.off('game:recovering', handleGameRecovering);
			socket.off('game:recovered', handleGameRecovered);
			window.removeEventListener('websocket:recovering', handleWebSocketRecovering);
			window.removeEventListener('websocket:ready', handleWebSocketReady);
		};
	}, [playerId, username]);

	useEffect(() => {
		if (!isFocused) {
			setShowInviteNotification(false);
			setInviteData(null);
			return;
		}

		if (!playerId || !username) return;

		const socket = getSocket(String(playerId), String(username));

		const handleInviteUser = (payload: any) => {
			const data = payload.data || payload;
			playSound('MessageRecieved'); // v1 init.service: socket 'inviteUser'
			setInviteData({
				name: data.name || data.inviterUsername,
				username: data.username || data.inviterUsername,
				table: data.table,
				table_name: data.table_name || data.tableName,
				legacyRoomId: data.legacyRoomId,
				url: data.url,
				inviteId: data.notification_id || data.inviteId,
			});
			setShowInviteNotification(true);
		};

		socket.on('inviteUser', handleInviteUser);
		return () => {
			socket.off('inviteUser', handleInviteUser);
		};
	}, [isFocused, playerId, username]);

	const handleAcceptInvite = () => {
		if (!inviteData || !playerId || !username) return;

		const socket = getSocket(String(playerId), String(username));
		const inviteSnapshot = inviteData;
		const responsePayload = {
			inviteId: inviteSnapshot.inviteId,
			response: 'accept',
			table: inviteSnapshot.table,
		};

		const handleInviteAccepted = (packet: any) => {
			if (!packet?.success) {
				toast.errorText(
					'Could not join table',
					packet?.error || 'The invitation is no longer available.',
				);
				return;
			}

			const urlParts = inviteSnapshot.url.split('/');
			const roomSlug = urlParts[2]?.split('?')[0] || 'vip';
			navigation.navigate('Game', {
				tableId: inviteSnapshot.table,
				legacyRoomId: inviteSnapshot.legacyRoomId,
				roomSlug,
				isTournamentRoom: false,
			});
		};

		socket.off('respondToInvite');
		socket.once('respondToInvite', handleInviteAccepted);
		socket.emit('svara:respondToInvite', responsePayload);
		setShowInviteNotification(false);
		setInviteData(null);
	};

	const handleRejectInvite = () => {
		if (!inviteData || !playerId || !username) return;

		const socket = getSocket(String(playerId), String(username));
		socket.emit('svara:respondToInvite', {
			inviteId: inviteData.inviteId,
			response: 'reject',
			table: inviteData.table,
		});
		setShowInviteNotification(false);
		setInviteData(null);
	};

	const handleRetry = () => {
		if (!playerId || !username) return;
		getSocket(String(playerId), String(username));
	};

	const handleBottomBarLayout = (event: { nativeEvent: { layout: { height: number } } }) => {
		const nextHeight = Math.ceil(event.nativeEvent.layout.height);
		setBottomBarHeight(currentHeight =>
			currentHeight === nextHeight ? currentHeight : nextHeight,
		);
		footerRef.current?.measureInWindow((_x, y) =>
			setFooterTop(current => (current === y ? current : y)),
		);
	};

	const rootStyle: ViewStyle = isWeb
		? ({
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				overflow: 'hidden',
			} as unknown as ViewStyle)
		: {
				position: 'absolute',
				top: 0,
				left: 0,
				width,
				height,
				bottom: 0,
			};
	const isHomeRedesignRoute = routeName === 'Main' && isMobileLayout;
	// Phone-shaped screens host the header 34 (of the 844 design frame) down, where the frames
	// draw the top bar under a status bar. It is the same header at the same size on every screen,
	// Home included, so the bar and the pills under it never move between screens.
	const headerTopOffset = isMobileLayout ? 34 * (height / 844) : 0;

	return (
		<FooterTopContext.Provider value={footerTop}>
			<HeaderTopOffsetContext.Provider value={headerTopOffset}>
				<View style={rootStyle}>
					<ImageBackground source={backgroundSource} style={[styles.full]} resizeMode='cover'>
						{showVipBackgroundOverlay ? (
							<View pointerEvents='none' style={styles.vipBackgroundOverlay}>
								<Image source={VipSubscriptionBackground} style={styles.full} resizeMode='cover' />
							</View>
						) : null}
						<StatusBar
							backgroundColor={theme.styles.container.backgroundColor}
							barStyle={dark ? 'light-content' : 'dark-content'}
						/>

						{isDesktopLayout ? (
							<View style={styles.full}>
								<View
									style={[
										styles.desktopContent,
										centerContent && styles.centerContent,
										{ top: desktop.contentTop, bottom: desktop.contentBottomInset },
									]}
								>
									{children}
								</View>
								{showChrome ? (
									<>
										<DesktopTopBar />
										<View style={styles.desktopFooterHost}>
											<AppFooter />
										</View>
									</>
								) : null}
							</View>
						) : (
							<SafeAreaView style={[styles.full]} edges={['top']}>
								{!showChrome ? null : isHomeRedesignRoute || fullBleed ? (
									<View style={[styles.fullBleedTopHost, { paddingTop: headerTopOffset }]}>
										<TopSection onOpenSidebar={() => setIsSidebarOpen(true)} />
									</View>
								) : (
									<View style={[styles.inFlowTopHost, { paddingTop: headerTopOffset }]}>
										<TopSection onOpenSidebar={() => setIsSidebarOpen(true)} />
									</View>
								)}

								<View
									style={[
										styles.content,
										(isHomeRedesignRoute || fullBleed) && styles.homeRedesignContent,
										centerContent && styles.centerContent,
										centerContent &&
											hasOverlayBottomBar &&
											bottomBarHeight > 0 && { paddingBottom: bottomBarHeight + 10 },
										!isWeb && contentMinHeight != null && { minHeight: contentMinHeight },
									]}
								>
									{children}
								</View>
								{!showChrome ? null : isHomeRedesignRoute || fullBleed ? (
									<View
										ref={footerRef}
										onLayout={handleBottomBarLayout}
										style={[
											styles.homeRedesignFooterHost,
											fullBleed && isAndroid && { paddingBottom: insets.bottom },
										]}
									>
										<AppFooter />
									</View>
								) : isAndroid ? (
									<View
										ref={footerRef}
										onLayout={handleBottomBarLayout}
										style={[styles.androidBottomBar, { paddingBottom: insets.bottom }]}
									>
										<AppFooter />
									</View>
								) : (
									<View ref={footerRef} onLayout={handleBottomBarLayout}>
										<AppFooter />
									</View>
								)}
							</SafeAreaView>
						)}

						{isSidebarOpen && (
							<Sidebar
								onClose={() => setIsSidebarOpen(false)}
								onOpenBonusGamesModal={() => {
									setIsBonusGamesModalOpen(true);
									setIsSidebarOpen(false);
								}}
							/>
						)}
						{isBonusGamesModalOpen && (
							<BonusGamesModal onClose={() => setIsBonusGamesModalOpen(false)} />
						)}
						{/* Payment Status Modal */}
						{paymentData.status && (
							<PaymentStatusModal
								visible={showPaymentModal}
								status={paymentData.status}
								packageInfo={paymentData.packageInfo}
								donation={paymentData.donation}
								onClose={handleClosePaymentModal}
								autoDismiss={true}
							/>
						)}
					</ImageBackground>

					<GameRecoveryModal
						visible={showRecoveryModal}
						onRetry={handleRetry}
						autoRetry={true}
						maxRetries={5}
					/>
					{showInviteNotification && inviteData && (
						<InviteNotificationModal
							visible={showInviteNotification}
							inviteData={inviteData}
							onAccept={handleAcceptInvite}
							onReject={handleRejectInvite}
						/>
					)}
				</View>
			</HeaderTopOffsetContext.Provider>
		</FooterTopContext.Provider>
	);
};

// Memoised so a screen re-rendering with the same body - Home does so twice as it is left, for
// the stack change and for the blur - does not redraw its header and footer along with it. The
// footer still follows the stack on its own, through its navigation-state subscription.
export const MainLayout = React.memo(MainLayoutView);

const styles = StyleSheet.create({
	full: {
		flex: 1,

		// width: "100%",
		// height: "100%",
		// overflow: "hidden"
	},
	content: {
		paddingVertical: 10,
		flex: 1,
		minHeight: 0,
	},
	homeRedesignContent: {
		...StyleSheet.absoluteFillObject,
		paddingVertical: 0,
	},
	// Keeps the header above the screen under it, as it was before it gained this wrapper.
	inFlowTopHost: {
		zIndex: 30,
	},
	fullBleedTopHost: {
		zIndex: 60,
		elevation: 60,
	},
	vipBackgroundOverlay: {
		...StyleSheet.absoluteFillObject,
		zIndex: 0,
	},
	homeRedesignFooterHost: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 70,
		elevation: 70,
	},
	centerContent: {
		alignItems: 'center',
		justifyContent: 'center',
	},
	desktopContent: {
		position: 'absolute',
		left: 0,
		right: 0,
	},
	desktopFooterHost: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 70,
	},
	androidBottomBar: {
		position: 'absolute',
		left: 0,
		right: 0,
		bottom: 14,
	},
});
